import { z } from 'zod'

export const triageOpinionSchema = z
	.object({
		injured: z
			.object({
				name: z.enum(['valid', 'invalid', 'missing']).default('valid'),
				birthday: z
					.enum(['valid', 'invalid', 'missing'])
					.default('valid'),
				occupation: z
					.enum(['valid', 'invalid', 'missing'])
					.default('valid'),
				providedName: z.string().optional(),
				providedBirthday: z.string().optional(),
				providedOccupation: z.number().optional(),
			})
			.default({}),
		accident: z
			.object({
				date: z.enum(['valid', 'invalid', 'missing']).default('valid'),
				description: z
					.enum(['valid', 'invalid', 'missing'])
					.default('valid'),
				providedDate: z.string().optional(),
				providedDescription: z.string().optional(),
			})
			.default({}),
		process: z
			.object({
				bodyParts: z
					.array(
						z.object({
							id: z.string().optional(),
							bodyPartId: z
								.string()
								.min(1, { message: 'Required' })
								.transform((elem) => parseInt(elem, 10)),
							injuryId: z
								.string()
								.min(1, { message: 'Required' })
								.transform((elem) => parseInt(elem, 10)),
							lateralityId: z
								.string()
								.min(1, { message: 'Required' })
								.transform((elem) => parseInt(elem, 10))
								.refine(
									(value) => {
										return !isNaN(value)
									},
									{
										message: `Required`,
										path: ['lateralityId'],
									}
								),
							primary: z.boolean(),
						})
					)
					.min(1, { message: 'Required' }),
				biologicRisk: z.boolean(),
				acutePhase: z.boolean().optional(),
				acutePhaseNotificationDate: z.string().optional(),
				hasPreviousSurgery: z.boolean().optional(),
				complexity: z.string(),
				duration: z.number().min(1).max(4),
				transfer: z.boolean(),
				transferActions: z
					.object({
						requestOpinion: z.boolean().default(false),
						notifyManagement: z.boolean().default(false),
						insertTransferRequest: z.boolean().default(false),
					})
					.optional(),
				transferOpinion: z
					.object({
						medicalOpinionId: z.string().optional(),
						description: z.string().optional(),
						priority: z.number().optional(),
						targetUser: z.string().optional(),
					})
					.optional(),
				transferRequest: z
					.object({
						specialty: z.string().optional(),
						reason: z.string().optional(),
					})
					.optional(),

				injuryMechanism: z
					.preprocess(
						(val) =>
							val &&
							typeof val === 'object' &&
							Object.keys(val).length === 0
								? undefined
								: val,
						z
							.object({
								mechanismType: z.string(),
								diagnosis: z.boolean().optional(),
								causalLink: z.string().optional(),
							})
							.strict()
							.optional()
					)
					.transform((data) => {
						if (!data || Object.keys(data).length === 0) {
							return undefined
						}

						const { mechanismType, diagnosis, causalLink } = data
						if (mechanismType === 'invalid') {
							return { mechanismType }
						}

						if (mechanismType !== 'invalid' && !diagnosis) {
							return {
								mechanismType,
								diagnosis,
							}
						}

						return { mechanismType, diagnosis, causalLink }
					}),
			})
			.strict(),
		observations: z.string().optional(),
		appointmentsProtocol: z
			.array(
				z.object({
					quantity: z.number().min(1),
					specialtyId: z.string(),
					serviceProviderId: z.string().optional(),
				})
			)
			.optional(),
		medicalActsProtocol: z
			.array(
				z.object({
					quantity: z.number().min(1),
					medicalActId: z.string(),
					serviceProviderId: z.string().optional(),
				})
			)
			.optional(),
	})
	.superRefine((data, ctx) => {
		if (data.injured.name === 'invalid' && !data.injured.providedName) {
			ctx.addIssue({
				path: ['injured', 'providedName'],
				code: z.ZodIssueCode.custom,
				message: 'Required',
			})
		}
		if (
			data.injured.birthday === 'invalid' &&
			!data.injured.providedBirthday
		) {
			ctx.addIssue({
				path: ['injured', 'providedBirthday'],
				code: z.ZodIssueCode.custom,
				message: 'Required',
			})
		}
		if (
			data.injured.occupation === 'invalid' &&
			!data.injured.providedOccupation
		) {
			ctx.addIssue({
				path: ['injured', 'providedOccupation'],
				code: z.ZodIssueCode.custom,
				message: 'Required',
			})
		}
		if (data.accident.date === 'invalid' && !data.accident.providedDate) {
			ctx.addIssue({
				path: ['accident', 'providedDate'],
				code: z.ZodIssueCode.custom,
				message: 'Required',
			})
		}
		if (
			data.accident.description === 'invalid' &&
			!data.accident.providedDescription
		) {
			ctx.addIssue({
				path: ['accident', 'providedDescription'],
				code: z.ZodIssueCode.custom,
				message: 'Required',
			})
		}
		if (
			data.process.injuryMechanism?.mechanismType !== undefined &&
			data.process.injuryMechanism?.mechanismType !== 'invalid' &&
			data.process.injuryMechanism?.diagnosis &&
			!data.process.injuryMechanism?.causalLink
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Required',
				path: ['process', 'injuryMechanism', 'causalLink'],
			})
		}
		if (
			data.process.bodyParts.length > 1 &&
			!data.process.bodyParts.some((bp) => bp.primary)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Obrigatório indicar a lesão principal',
				path: ['process', 'bodyParts'],
			})
		}
		if (
			data.process.transferActions?.requestOpinion &&
			!data.process.transferOpinion?.description
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Required',
				path: ['process', 'transferOpinion', 'description'],
			})
		}

		if (
			data.process.transferActions?.insertTransferRequest &&
			!data.process.transferRequest?.specialty
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Required',
				path: ['process', 'transferRequest', 'specialty'],
			})
		}
		if (
			data.process.transferActions?.insertTransferRequest &&
			!data.process.transferRequest?.reason
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Required',
				path: ['process', 'transferRequest', 'reason'],
			})
		}
	})
